import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, useTheme, Button, IconButton, InputBase,
    Chip, CircularProgress, Table, TableHead, TableBody, TableRow, TableCell,
    Pagination, Select, MenuItem, FormControl, InputLabel, Collapse,
    Autocomplete, TextField, Tooltip,
} from "@mui/material";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import QuestionRender from "../../components/QuestionRender";
import { useNavigate } from "react-router-dom";
import api from '../../utils/v2Api';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AddIcon from '@mui/icons-material/Add';

const TYPE_OPTIONS = [
    { value: '', label: 'All Types' },
    { value: 'mcq', label: 'MCQ' },
    { value: 'mc', label: 'MC' },
    { value: 'saq', label: 'SAQ' },
    { value: 'sa', label: 'SA' },
    { value: 'fill_in_blank', label: 'Fill in Blank' },
    { value: 'true_false', label: 'True/False' },
    { value: 'matching', label: 'Matching' },
];

const TYPE_COLORS = {
    mcq: 'greenAccent', mc: 'greenAccent',
    saq: 'blueAccent', sa: 'blueAccent',
    fill_in_blank: 'grey', true_false: 'grey', matching: 'grey',
};

const LIMIT = 50;

const QuestionBank = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const navigate = useNavigate();

    const [questions, setQuestions] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [searchDebounced, setSearchDebounced] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [semesterFilter, setSemesterFilter] = useState('');
    const [semesters, setSemesters] = useState([]);
    const [expandedId, setExpandedId] = useState(null);
    const [expandedData, setExpandedData] = useState(null);
    const [expandLoading, setExpandLoading] = useState(false);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => setSearchDebounced(search), 300);
        return () => clearTimeout(timer);
    }, [search]);

    // Reset page when filters change
    useEffect(() => { setPage(1); }, [searchDebounced, typeFilter, semesterFilter]);

    // Fetch questions
    const fetchQuestions = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, limit: LIMIT };
            if (searchDebounced) params.search = searchDebounced;
            if (typeFilter) params.question_type = typeFilter;
            if (semesterFilter) params.semester = semesterFilter;
            const res = await api.get('/question-bank', params);
            setQuestions(res.data.data);
            setTotal(res.data.total);
        } catch (err) {
            console.error('Failed to fetch questions:', err);
        }
        setLoading(false);
    }, [page, searchDebounced, typeFilter, semesterFilter]);

    useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

    // Fetch unique semesters on mount
    useEffect(() => {
        const fetchSemesters = async () => {
            try {
                // Fetch a large batch to extract unique semesters from metadata
                const res = await api.get('/question-bank', { limit: 1, page: 1 });
                // We'll build semesters from what we see — or just hardcode known ones
                // For now, use known semesters from the seeded data
                setSemesters([
                    '2024_Spring', '2024_Summer', '2024_Fall',
                    '2025_Spring', '2025_Summer', '2025_Fall',
                    '2026_Spring',
                ]);
            } catch { /* ignore */ }
        };
        fetchSemesters();
    }, []);

    // Expand/collapse row
    const handleRowClick = async (id) => {
        if (expandedId === id) {
            setExpandedId(null);
            setExpandedData(null);
            return;
        }
        setExpandedId(id);
        setExpandedData(null);
        setExpandLoading(true);
        try {
            const res = await api.get(`/question-bank/${id}`);
            setExpandedData(res.data.data);
        } catch (err) {
            console.error('Failed to fetch question details:', err);
        }
        setExpandLoading(false);
    };

    const getTypeColor = (type) => {
        const colorKey = TYPE_COLORS[type] || 'grey';
        const shade = type === 'sa' || type === 'mc' ? 600 : 700;
        return colors[colorKey]?.[shade] || colors.grey[600];
    };

    const truncateText = (text, maxLen = 100) => {
        if (!text) return '—';
        if (text.length <= maxLen) return text;
        return text.substring(0, maxLen) + '...';
    };

    const totalPages = Math.ceil(total / LIMIT);

    return (
        <Box m="20px">
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Header title="Question Bank" subtitle={`${total.toLocaleString()} questions`} />
                <Box display="flex" gap={1}>
                    <Button
                        variant="outlined"
                        startIcon={<LocalOfferIcon />}
                        onClick={() => navigate('/admin/tags')}
                    >
                        Tags
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/admin/tests-v2')}
                    >
                        Test Library
                    </Button>
                </Box>
            </Box>

            {/* Toolbar */}
            <Box display="flex" gap={2} mb={3} alignItems="center" flexWrap="wrap">
                <Box
                    display="flex"
                    bgcolor={colors.primary[600]}
                    borderRadius="3px"
                    boxShadow={1}
                    sx={{ maxWidth: 400, flex: 1, minWidth: 200 }}
                >
                    <InputBase
                        sx={{ ml: 2, flex: 1 }}
                        placeholder="Search questions..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <IconButton type="button" sx={{ p: 1 }}>
                        <SearchIcon />
                    </IconButton>
                </Box>

                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Type</InputLabel>
                    <Select
                        value={typeFilter}
                        label="Type"
                        onChange={(e) => setTypeFilter(e.target.value)}
                    >
                        {TYPE_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Semester</InputLabel>
                    <Select
                        value={semesterFilter}
                        label="Semester"
                        onChange={(e) => setSemesterFilter(e.target.value)}
                    >
                        <MenuItem value="">All Semesters</MenuItem>
                        {semesters.map((s) => (
                            <MenuItem key={s} value={s}>{s.replace('_', ' ')}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Table */}
            {loading ? (
                <Box display="flex" justifyContent="center" py={8}>
                    <CircularProgress />
                </Box>
            ) : questions.length === 0 ? (
                <Box display="flex" justifyContent="center" py={8}>
                    <Typography color={colors.grey[400]}>No questions found</Typography>
                </Box>
            ) : (
                <>
                    <Table size="small" sx={{ bgcolor: colors.primary[600], borderRadius: 1, tableLayout: 'fixed' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700, width: 50 }}>#</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: 90 }}>Type</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: 220 }}>Tags</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Question</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: 100 }}>Answer</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: 200 }}>Source</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: 40 }} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {questions.map((q, idx) => (
                                <React.Fragment key={q.id}>
                                    <TableRow
                                        hover
                                        sx={{ cursor: 'pointer', '& td': { borderBottom: expandedId === q.id ? 'none' : undefined } }}
                                        onClick={() => handleRowClick(q.id)}
                                    >
                                        <TableCell>
                                            <Typography variant="body2" color={colors.grey[300]}>
                                                {(page - 1) * LIMIT + idx + 1}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={q.question_type?.toUpperCase()}
                                                size="small"
                                                sx={{
                                                    bgcolor: getTypeColor(q.question_type),
                                                    color: '#fff',
                                                    fontWeight: 600,
                                                    fontSize: '0.7rem',
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" gap={0.5} flexWrap="wrap">
                                                {(q.v2_tags || []).slice(0, 3).map((tag) => (
                                                    <Chip
                                                        key={tag.id}
                                                        label={tag.name}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ fontSize: '0.65rem', height: 20 }}
                                                    />
                                                ))}
                                                {(q.v2_tags || []).length > 3 && (
                                                    <Chip label={`+${q.v2_tags.length - 3}`} size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            <Typography variant="body2" noWrap>
                                                {truncateText(q.question_prompt, 120)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" noWrap color={colors.greenAccent[400]} fontWeight={600}>
                                                {truncateText(q.answer, 20)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color={colors.grey[400]} noWrap>
                                                {q.metadata?.source_semester?.replace('_', ' ') || '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {expandedId === q.id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                        </TableCell>
                                    </TableRow>

                                    {/* Expanded detail row */}
                                    <TableRow>
                                        <TableCell colSpan={7} sx={{ p: 0, border: 'none' }}>
                                            <Collapse in={expandedId === q.id} timeout="auto" unmountOnExit>
                                                <Box sx={{ p: 3, bgcolor: colors.primary[700], borderBottom: `1px solid ${colors.grey[700]}` }}>
                                                    {expandLoading ? (
                                                        <Box display="flex" justifyContent="center" py={2}>
                                                            <CircularProgress size={24} />
                                                        </Box>
                                                    ) : expandedData ? (
                                                        <ExpandedQuestion data={expandedData} colors={colors} onTagChange={fetchQuestions} />
                                                    ) : null}
                                                </Box>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <Box display="flex" justifyContent="center" mt={3}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(_, val) => {
                                    setPage(val);
                                    setExpandedId(null);
                                    setExpandedData(null);
                                }}
                                color="secondary"
                                shape="rounded"
                            />
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

/** Expanded question detail */
const ExpandedQuestion = ({ data, colors, onTagChange }) => {
    const options = data.v2_bank_question_options || [];
    const parts = data.v2_bank_question_parts || [];
    const sortedOptions = [...options].sort((a, b) => (a.order || 0) - (b.order || 0));
    const tags = data.v2_tags || [];
    const [allTags, setAllTags] = useState([]);
    const [addingTag, setAddingTag] = useState(false);

    useEffect(() => {
        if (addingTag && allTags.length === 0) {
            api.get('/tags/flat').then((res) => setAllTags(res.data.data || [])).catch(() => {});
        }
    }, [addingTag, allTags.length]);

    const handleAddTag = async (tag) => {
        if (!tag) return;
        try {
            await api.post(`/question-bank/${data.id}/tags`, { tag_id: tag.id });
            setAddingTag(false);
            onTagChange?.();
        } catch (err) {
            console.error('Failed to add tag:', err);
        }
    };

    const handleRemoveTag = async (tagId) => {
        try {
            await api.delete(`/question-bank/${data.id}/tags/${tagId}`);
            onTagChange?.();
        } catch (err) {
            console.error('Failed to remove tag:', err);
        }
    };

    return (
        <Box>
            {/* Tags */}
            <Box mb={2}>
                <Typography variant="caption" color={colors.grey[400]} fontWeight={600} mb={0.5} display="block">
                    TAGS
                </Typography>
                <Box display="flex" gap={0.8} flexWrap="wrap" alignItems="center">
                    {tags.map((tag) => {
                        const jt = tag.v2_bank_question_tags || {};
                        const isManual = jt.source === 'manual';
                        return (
                            <Tooltip key={tag.id} title={`${jt.source || 'ai'} — confidence: ${jt.confidence ?? '—'}`}>
                                <Chip
                                    label={tag.name}
                                    size="small"
                                    onDelete={() => handleRemoveTag(tag.id)}
                                    sx={{
                                        borderColor: isManual ? colors.greenAccent[500] : colors.blueAccent[500],
                                        color: isManual ? colors.greenAccent[400] : colors.blueAccent[400],
                                        fontSize: '0.75rem',
                                    }}
                                    variant="outlined"
                                />
                            </Tooltip>
                        );
                    })}
                    {addingTag ? (
                        <Autocomplete
                            size="small"
                            options={allTags.filter((t) => !tags.some((et) => et.id === t.id))}
                            getOptionLabel={(opt) => opt.path || opt.name}
                            onChange={(_, val) => handleAddTag(val)}
                            onBlur={() => setAddingTag(false)}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Search tags..." autoFocus sx={{ minWidth: 200 }} />
                            )}
                            sx={{ minWidth: 200 }}
                            openOnFocus
                        />
                    ) : (
                        <Chip
                            icon={<AddIcon />}
                            label="Add Tag"
                            size="small"
                            variant="outlined"
                            onClick={() => setAddingTag(true)}
                            sx={{ cursor: 'pointer' }}
                        />
                    )}
                </Box>
            </Box>

            {/* Full prompt */}
            <Box mb={2}>
                <Typography variant="caption" color={colors.grey[400]} fontWeight={600} mb={0.5} display="block">
                    QUESTION
                </Typography>
                <QuestionRender content={data.question_prompt} sx={{ fontSize: '0.95rem', lineHeight: 1.7 }} />
            </Box>

            {/* MCQ Options */}
            {sortedOptions.length > 0 && (
                <Box mb={2}>
                    <Typography variant="caption" color={colors.grey[400]} fontWeight={600} mb={0.5} display="block">
                        OPTIONS
                    </Typography>
                    {sortedOptions.map((opt) => {
                        const isCorrect = data.answer && opt.option_label === data.answer;
                        return (
                            <Box
                                key={opt.id}
                                sx={{
                                    display: 'flex',
                                    gap: 1.5,
                                    mb: 0.8,
                                    p: 1,
                                    borderRadius: 1,
                                    bgcolor: isCorrect ? `${colors.greenAccent[900]}40` : 'transparent',
                                }}
                            >
                                <Typography fontWeight={600} sx={{ minWidth: 24 }}>
                                    {opt.option_label}.
                                </Typography>
                                <Box flex={1}>
                                    <QuestionRender content={opt.option_text} />
                                </Box>
                                {isCorrect && (
                                    <CheckCircleIcon sx={{ color: colors.greenAccent[400], fontSize: 20, mt: 0.2 }} />
                                )}
                            </Box>
                        );
                    })}
                </Box>
            )}

            {/* FRQ Parts */}
            {parts.length > 0 && (
                <Box mb={2}>
                    <Typography variant="caption" color={colors.grey[400]} fontWeight={600} mb={0.5} display="block">
                        PARTS
                    </Typography>
                    {parts.map((part) => (
                        <PartItem key={part.id} part={part} colors={colors} depth={0} />
                    ))}
                </Box>
            )}

            {/* Answer */}
            <Box mb={2}>
                <Typography variant="caption" color={colors.grey[400]} fontWeight={600} mb={0.5} display="block">
                    ANSWER
                </Typography>
                <Box sx={{ p: 1.5, bgcolor: colors.primary[600], borderRadius: 1 }}>
                    <QuestionRender content={data.answer || '—'} />
                </Box>
            </Box>

            {/* Metadata */}
            {data.metadata && (
                <Box display="flex" gap={2} flexWrap="wrap">
                    {data.metadata.source_exam && (
                        <Chip
                            label={data.metadata.source_exam.replace(/__/g, ' / ').replace(/_/g, ' ')}
                            size="small"
                            variant="outlined"
                        />
                    )}
                    {data.metadata.source_semester && (
                        <Chip
                            label={data.metadata.source_semester.replace('_', ' ')}
                            size="small"
                            variant="outlined"
                        />
                    )}
                    {data.metadata.question_number && (
                        <Chip
                            label={`Q${data.metadata.question_number}`}
                            size="small"
                            variant="outlined"
                        />
                    )}
                </Box>
            )}
        </Box>
    );
};

/** Recursive part renderer */
const PartItem = ({ part, colors, depth }) => (
    <Box sx={{ ml: depth * 3, mb: 1, p: 1.5, bgcolor: colors.primary[600], borderRadius: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
            <Typography fontWeight={600}>
                {part.part_label && `(${part.part_label})`} {part.criteria_name || ''}
            </Typography>
            <Chip label={`${part.max_points} pt${part.max_points !== 1 ? 's' : ''}`} size="small" variant="outlined" />
        </Box>
        {part.rubric_text && (
            <Typography variant="body2" color={colors.grey[300]} sx={{ mt: 0.5 }}>
                {part.rubric_text}
            </Typography>
        )}
        {part.subparts?.map((sub) => (
            <PartItem key={sub.id} part={sub} colors={colors} depth={depth + 1} />
        ))}
    </Box>
);

export default QuestionBank;
